import React, { useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Link as ReactRouterLink, useSearchParams } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import {
  PasswordResetEmailType,
  PasswordResetPasswordType
} from '../../types/types';
import { emailValidationRegex } from '../../constants/regex';
import { useNetwork } from '../../utils/network';
import { useShowSnackbar } from '../../utils/snackbar';
import PasswordField from '../../components/PasswordField';
import './SetPassword.css'
import { AuthShell, AuthCard } from '../../components/AuthShell'

type Props = {};

export const SetPassword = (props: Props) => {
  const { get, post } = useNetwork()
  const { showSnackbar } = useShowSnackbar()
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const hasResetReferralLink: string = searchParams.get('code') || '';

  const [isPasswordResetSuccessful, setIsPasswordResetSuccessful] =
    useState<boolean>(false);

  /* Is this link ACTIVATING an account (it has no password yet — a sign-up, or a
     member a partner such as Empire sent us) or resetting one? An activation must
     agree to the terms, as the sign-up form does; the server refuses it otherwise.
     Until the probe answers — or if it cannot — the box is shown, which is the
     safe side: a tick that was not needed costs one click. */
  const [requiresTerms, setRequiresTerms] = useState<boolean>(true);
  const [linkValid, setLinkValid] = useState<boolean>(true);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  useEffect(() => {
    if (!hasResetReferralLink) return;
    let cancelled = false;
    get(`/auth/set-password/${encodeURIComponent(hasResetReferralLink)}`)
      .then((info: { valid?: boolean; requiresTerms?: boolean }) => {
        if (cancelled || !info) return;
        setLinkValid(info.valid !== false);
        setRequiresTerms(info.valid === false ? false : info.requiresTerms !== false);
      })
      .catch(() => {
        /* an older server has no probe: keep the box, the server decides */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResetReferralLink]);

  const {
    handleSubmit: handleEmailSubmit,
    control: emailControl,
    formState: { errors: emailErrors }
  } = useForm<PasswordResetEmailType>();

  const {
    handleSubmit: handlePasswordSubmit,
    control: passwordControl,
    formState: { errors: passwordErrors }
  } = useForm<PasswordResetPasswordType>();

  const onEmailSubmit: SubmitHandler<PasswordResetEmailType> = async (data) => {
    try {
      await post('/auth/forgot-password', { email: data.email })

      setIsPasswordResetSuccessful(true);
      setIsEmailSent(true);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.data?.message || 'Something went wrong. Please try again.', 'error');
    }
  };

  const onPasswordSubmit: SubmitHandler<PasswordResetPasswordType> = async (
    data
  ) => {
    try {
      await post('/auth/set-password', {
        password: data.password,
        activationCode: hasResetReferralLink,
        termsAccepted
      })

      setIsPasswordResetSuccessful(true);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.data?.message || 'Something went wrong. Please try again.', 'error');
    }
  };

  return (
    <AuthShell>
      <AuthCard>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {!hasResetReferralLink && !isEmailSent && (
            <>
              <Typography component="h1" variant="h4">
                Password Reset
              </Typography>
              <Box
                sx={{
                  mt: '5rem',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  width: '100%'
                }}
              >
                <form onSubmit={handleEmailSubmit(onEmailSubmit)}>
                  <Controller
                    name="email"
                    control={emailControl}
                    rules={{
                      required: true,
                      pattern: emailValidationRegex
                    }}
                    render={({ field }) => (
                      <TextField
                        id="email"
                        margin="normal"
                        required
                        fullWidth
                        label="Email"
                        autoFocus
                        autoComplete="email"
                        error={Boolean(emailErrors.email)}
                        helperText={
                          emailErrors.email && 'Please enter a valid email'
                        }
                        {...field}
                      />
                    )}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                  >
                    Send email
                  </Button>
                  <Grid container>
                    <Grid item>
                      <Link
                        component={ReactRouterLink}
                        to="/login"
                        variant="body2"
                      >
                        {'Back to Login'}
                      </Link>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            </>
          )}
          {!hasResetReferralLink && isEmailSent && (
            <>
              <Typography component="h1" variant="h4">
                Password Reset
              </Typography>
              <Box sx={{ mt: '5rem' }}>
                <Typography variant="body1">
                  If user exists an email has been sent. Please check your email for password
                  reset link
                </Typography>
              </Box>
              <Grid sx={{ width: '100%', mt: '2rem' }}>
                <Grid item sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Link component={ReactRouterLink} to="/login" variant="body2">
                    <Button variant="contained">Back to Login</Button>
                  </Link>
                </Grid>
              </Grid>
            </>
          )}
          {hasResetReferralLink && !isPasswordResetSuccessful && !linkValid && (
            <>
              <Typography component="h1" variant="h4">
                Link expired
              </Typography>
              <Box sx={{ mt: '3rem' }}>
                <Typography variant="body1">
                  This set-password link has expired or was already used. Enter your
                  email below and we will send you a fresh one.
                </Typography>
                <form onSubmit={handleEmailSubmit(onEmailSubmit)}>
                  <Controller
                    name="email"
                    control={emailControl}
                    rules={{ required: true, pattern: emailValidationRegex }}
                    render={({ field }) => (
                      <TextField
                        id="email"
                        margin="normal"
                        required
                        fullWidth
                        label="Email"
                        autoComplete="email"
                        error={Boolean(emailErrors.email)}
                        helperText={emailErrors.email && 'Please enter a valid email'}
                        {...field}
                      />
                    )}
                  />
                  <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
                    Send me a new link
                  </Button>
                </form>
              </Box>
            </>
          )}
          {hasResetReferralLink && !isPasswordResetSuccessful && linkValid && (
            <>
              <Typography component="h1" variant="h4">
                Set Password
              </Typography>
              <Box
                sx={{
                  mt: '5rem',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  width: '100%'
                }}
              >
                <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                  <Controller
                    name="password"
                    control={passwordControl}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <PasswordField
                        margin="normal"
                        required
                        fullWidth
                        label="Password"
                        id="password"
                        autoFocus
                        error={Boolean(passwordErrors.confirmPassword)}
                        helperText={
                          passwordErrors.confirmPassword &&
                          'Passwords do not match'
                        }
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name="confirmPassword"
                    control={passwordControl}
                    rules={{
                      required: true
                    }}
                    render={({ field }) => (
                      <PasswordField
                        margin="normal"
                        required
                        fullWidth
                        label="Confirm Password"
                        id="confirmPassword"
                        autoFocus
                        error={Boolean(passwordErrors.confirmPassword)}
                        helperText={
                          passwordErrors.confirmPassword &&
                          'Passwords do not match'
                        }
                        {...field}
                      />
                    )}
                  />
                  {requiresTerms && (
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Checkbox
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                        />
                      }
                      label={
                        <Typography>
                          I have read and agree to the{' '}
                          <Link
                            href="https://incrypt-public-assets-global.s3.ap-southeast-2.amazonaws.com/documents/terms_and_conditions_05_08_2026.docx"
                            rel="noopener noreferrer"
                            variant="body2"
                            target="_blank"
                            style={{ fontSize: '16px' }}
                          >
                            terms and conditions
                          </Link>
                        </Typography>
                      }
                      labelPlacement="end"
                    />
                  )}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={requiresTerms && !termsAccepted}
                    sx={{ mt: 3, mb: 2 }}
                  >
                    Submit
                  </Button>
                </form>
              </Box>
            </>
          )}

          {hasResetReferralLink && isPasswordResetSuccessful && (
            <>
              <Typography component="h1" variant="h4">
                {requiresTerms ? 'You are all set' : 'Password Reset'}
              </Typography>
              <Box sx={{ mt: '5rem' }}>
                <Typography variant="body1">
                  {requiresTerms
                    ? 'Your password is set and your account is ready. Log in to get started.'
                    : 'Password has been reset. Please login with your new password'}
                </Typography>
                <Grid sx={{ width: '100%', mt: '2rem' }}>
                  <Grid item sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Link
                      component={ReactRouterLink}
                      to="/login"
                      variant="body2"
                    >
                      <Button variant="contained">Back to Login</Button>
                    </Link>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </Box>
      </AuthCard>
    </AuthShell>
  );
};
